import { firstValidationMessage, toUserMessage } from '@/utils/error';
import { humanizeCode, normalizeCode } from '@/utils/billingLabels';
import { formatCurrency } from '@/utils/currency';
import { SplitModeEnum } from '@/types/composition-functions';

/**
 * Logic behind fee splitting (parent dashboard → FeeSplitManagement.vue and
 * the campus consent card): plain names for split modes and statuses,
 * validation of split rules, what a rule applies to, and the consequence text
 * shown before switching things off.
 */

// ── Names ──

const SPLIT_MODE_LABELS: Record<string, string> = {
  percentage: 'Percentage split',
  fixed: 'Fixed amounts',
  base_markup: 'HQ base + campus markup',
};

const SPLIT_MODE_HELP: Record<string, string> = {
  percentage: 'Each payment is shared by percentage, e.g. 30% to HQ and 70% to the campus.',
  fixed: 'HQ and the campus each receive a set amount from every payment.',
  base_markup: 'HQ receives a fixed base amount per student; the campus keeps whatever it charges on top.',
};

export function splitModeLabel(mode: string | null | undefined): string {
  return SPLIT_MODE_LABELS[normalizeCode(mode)] ?? humanizeCode(mode);
}

export function splitModeHelp(mode: string | null | undefined): string {
  return SPLIT_MODE_HELP[normalizeCode(mode)] ?? '';
}

/**
 * The GraphQL enum value for a split mode as the API returned it ('Percentage',
 * 'percentage' or 'PERCENTAGE'), so it can be sent back as a variable.
 */
export function splitModeEnumValue(mode: string | null | undefined): SplitModeEnum {
  const code = normalizeCode(mode);
  if (code === 'fixed') return SplitModeEnum.Fixed;
  if (code === 'base_markup') return SplitModeEnum.BaseMarkup;
  return SplitModeEnum.Percentage;
}

export function splitModeIs(mode: string | null | undefined, target: 'percentage' | 'fixed' | 'base_markup'): boolean {
  return normalizeCode(mode) === target;
}

const GATEWAY_SPLIT_STATUS: Record<string, { label: string; severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' }> = {
  pending: { label: 'Waiting to send', severity: 'warn' },
  sent: { label: 'Sent to the gateway', severity: 'info' },
  confirmed: { label: 'Confirmed', severity: 'success' },
  failed: { label: 'Failed', severity: 'danger' },
};

/** Where a split stands with the payment gateway, in plain words. */
export function gatewaySplitStatus(status: string | null | undefined): { label: string; severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' } {
  if (!status) return { label: 'Recorded only', severity: 'secondary' };
  return GATEWAY_SPLIT_STATUS[normalizeCode(status)] ?? { label: humanizeCode(status), severity: 'info' };
}

// ── Rule targets ──

// @allow-handwritten UI-only lookup of names the rule list needs; never crosses the wire
export interface BillableNameLookup {
  programmes: Array<{ id: string | number; name: string; school_name?: string | null }>;
  classrooms: Array<{ id: string | number; name: string; school_name?: string | null }>;
}

// @allow-handwritten UI-only subset of a split rule used for labels; never crosses the wire
export interface RuleTargetFields {
  billable_type?: string | null;
  billable_id?: string | number | null;
  billItemTemplate?: { name: string } | null;
  billItemCategory?: { name: string } | null;
}

/** "Programme: ND Accounting (Main Campus)" / "Level: JSS 1 (East)" — '' when the rule has no target. */
export function ruleTargetLabel(rule: RuleTargetFields, names: BillableNameLookup): string {
  if (!rule.billable_type || rule.billable_id === null || rule.billable_id === undefined) return '';
  const isProgramme = normalizeCode(rule.billable_type) === 'programme';
  const label = isProgramme ? 'Programme' : 'Level';
  const list = isProgramme ? names.programmes : names.classrooms;
  const match = list.find((item) => String(item.id) === String(rule.billable_id));
  if (!match) return `${label}: loading name…`;
  return match.school_name ? `${label}: ${match.name} (${match.school_name})` : `${label}: ${match.name}`;
}

/** Which fees a rule covers: a fee item, a category, or every fee. */
export function ruleFeeLabel(rule: RuleTargetFields): string {
  if (rule.billItemTemplate?.name) return rule.billItemTemplate.name;
  if (rule.billItemCategory?.name) return rule.billItemCategory.name;
  return 'All fees';
}

/** Short name for a rule, e.g. on a campus's override button. */
export function ruleShortLabel(rule: RuleTargetFields, names: BillableNameLookup): string {
  const target = ruleTargetLabel(rule, names);
  return target ? `${ruleFeeLabel(rule)} · ${target}` : ruleFeeLabel(rule);
}

export function overrideButtonLabel(rule: RuleTargetFields, names: BillableNameLookup, hasOverride: boolean): string {
  return `${hasOverride ? 'Edit override' : 'Override'}: ${ruleShortLabel(rule, names)}`;
}

// ── Validation ──

/** 100 − value, rounded to 2 decimals; null when value is empty. */
export function percentageComplement(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const clamped = Math.min(100, Math.max(0, value));
  return Math.round((100 - clamped) * 100) / 100;
}

/**
 * HQ and campus percentages must add up to exactly 100. Anything less is
 * quietly given to HQ by the server, so the screen would show a different
 * split from the one that actually happens.
 */
export function percentageSplitProblem(hq: number | null | undefined, campus: number | null | undefined, options: { allowEmpty?: boolean } = {}): string {
  const hqEmpty = hq === null || hq === undefined;
  const campusEmpty = campus === null || campus === undefined;
  if (hqEmpty && campusEmpty) return options.allowEmpty ? '' : 'Enter the HQ and campus percentages.';
  const total = Math.round(((hq ?? 0) + (campus ?? 0)) * 100) / 100;
  if (total !== 100) return `HQ and campus must add up to 100% — they add up to ${total}% now.`;
  return '';
}

export function fixedSplitProblem(hq: number | null | undefined, campus: number | null | undefined): string {
  return (hq ?? 0) > 0 || (campus ?? 0) > 0 ? '' : 'Enter an HQ amount, a campus amount, or both.';
}

export function baseMarkupProblem(base: number | null | undefined): string {
  return (base ?? 0) > 0 ? '' : 'Enter the amount HQ receives per student.';
}

const SERVER_RULE_MESSAGES: Array<[RegExp, string]> = [
  [/sum of hq_percentage and campus_percentage cannot exceed 100/i, 'HQ and campus percentages can\'t add up to more than 100%.'],
  [/percentage split rules require/i, 'Enter the HQ and campus percentages.'],
  [/fixed split rules require/i, 'Enter an HQ amount, a campus amount, or both.'],
  [/base markup split rules require/i, 'Enter the amount HQ receives per student.'],
];

/** Toast detail for a failed fee-split save, without raw field names like hq_percentage. */
export function feeSplitErrorMessage(error: unknown, fallback = 'Nothing was saved. Please try again.'): string {
  const message = (error as { message?: unknown } | null)?.message;
  const text = typeof message === 'string' ? message : '';
  for (const [pattern, friendly] of SERVER_RULE_MESSAGES) {
    if (pattern.test(text)) return friendly;
  }
  if (firstValidationMessage(error)) return 'Some details need fixing — see the highlighted fields.';
  return toUserMessage(error, fallback);
}

// ── Split terms shown in the rule table ──

// @allow-handwritten UI-only subset of split amounts; never crosses the wire
export interface SplitTerms {
  hq_amount?: number | null;
  campus_amount?: number | null;
  hq_percentage?: number | null;
  campus_percentage?: number | null;
  base_amount?: number | null;
  campus_price?: number | null;
}

export function splitTermsText(mode: string | null | undefined, terms: SplitTerms): string {
  if (splitModeIs(mode, 'fixed')) {
    return `HQ ${formatCurrency(terms.hq_amount ?? 0)} · Campus ${formatCurrency(terms.campus_amount ?? 0)}`;
  }
  if (splitModeIs(mode, 'percentage')) {
    return `HQ ${terms.hq_percentage ?? 0}% · Campus ${terms.campus_percentage ?? 0}%`;
  }
  const campusFee = terms.campus_price ? formatCurrency(terms.campus_price) : 'set per campus';
  return `HQ base ${formatCurrency(terms.base_amount ?? 0)} · Campus fee ${campusFee}`;
}

// ── Consequences ──

export function feeSplitOffMessage(consentedCount: number): string {
  const who = consentedCount === 1 ? 'the 1 campus that has consented' : `the ${consentedCount} campuses that have consented`;
  return `New payments will stop being shared for ${consentedCount ? who : 'every campus'}: each campus keeps all of `
    + 'its own payments. Payments already shared are not changed. Your profiles and rules are kept, so you can turn it '
    + 'back on at any time.';
}

export function withdrawConsentMessage(parentName: string): string {
  return `New fee payments at your campus will no longer be shared with ${parentName} — your campus keeps all of them. `
    + 'Payments already shared are not changed. You can give consent again at any time.';
}

export function unassignMessage(schoolName: string, profileName: string): string {
  return `Remove ${schoolName} from "${profileName}"? New payments at ${schoolName} will no longer be shared using this profile. Payments already shared are not changed.`;
}

export function deleteProfileMessage(profileName: string, assignedCount: number): string {
  const campuses = assignedCount === 1 ? '1 campus' : `${assignedCount} campuses`;
  const impact = assignedCount
    ? ` It is used by ${campuses}; new payments there will no longer be shared by it.`
    : '';
  return `Delete the profile "${profileName}" and all its rules?${impact} Payments already shared are not changed. This can't be undone.`;
}

export function deleteRuleMessage(ruleLabel: string): string {
  return `Delete the rule for "${ruleLabel}"? New payments it covered will only be shared if another rule matches them. This can't be undone.`;
}

export function deleteSettlementAccountMessage(accountName: string, schoolName: string): string {
  return `Delete the account "${accountName}" (${schoolName})? Shared payments can no longer be paid out to it. This can't be undone.`;
}
